// What this app's pages ask of the API beyond the card itself: applying for a card, the password, ZumRails' hosted
// form. The shapes are the ones the legacy pages were written against; the calls go to /me and /auth.
import { action, api, BffError } from '@qor/web-shared';

export {
  cardApi,
  type ActionResult,
  type CardAccount,
  type CardAction,
  type Profile,
  type Transaction,
} from '@qor/web-shared';

/** {Mdp,Intercash,Gsc}OnboardResponse; `verification` when the processor still has to verify identity. */
export interface OnboardResult {
  success: boolean;
  message: string;
  providerUserReference: string | null;
  providerCardReference: string | null;
  verification?: KycState;
}

export type OnboardingProvider = 'mdp' | 'intercash' | 'gsc' | 'opencp';

/** {Mdp,Intercash,Gsc}OnboardRequest (dates as yyyy-MM-dd). */
export interface OnboardingForm {
  firstName: string;
  lastName: string;
  phoneNumber: string;
  dateOfBirth: string;
  placeOfBirth: string;
  gender: string;
  nationality: string;
  idType: string;
  idNumber: string;
  idExpiryDate: string;
  preferredLanguage: string;
  addressCountry: string;
  addressRegion: string;
  addressCity: string;
  addressStreet: string;
  addressHouseNumber: string;
  addressPostalCode: string | null;
  /** US SSN, where identity is verified on it (Central Payments). */
  ssn?: string;
}

/** Where identity verification stands at a processor that runs it itself (GET /me/kyc). */
export interface KycState {
  /** approved | questions_required | documents_required | under_review | denied, or null (nothing applied for) */
  status: string | null;
  questions: KycQuestion[];
  application: { status: string; message: string | null } | null;
}
export interface KycQuestion {
  id: number;
  question: string;
  answers: { id: number; answer: string }[];
}

/**
 * Apply for a card (POST /me/onboarding). The form's fields as the API names them; the processor is the program's,
 * so `provider` only chooses the page. The API answers with the card; the pages want its id as the reference.
 */
export const onboard = (_provider: OnboardingProvider, _cardholderId: string, form: OnboardingForm) =>
  action(
    (async (): Promise<OnboardResult> => {
      const result = await api<{
        status: string;
        message: string;
        last4?: string | null;
        kyc?: { status: string; questions: KycQuestion[] };
      }>('POST', '/me/onboarding', {
        body: {
          firstName: form.firstName,
          lastName: form.lastName,
          phone: form.phoneNumber,
          dateOfBirth: form.dateOfBirth,
          placeOfBirth: form.placeOfBirth || null,
          gender: form.gender || null,
          nationality: form.nationality || null,
          idType: form.idType || null,
          idNumber: form.idNumber || null,
          idExpiryDate: form.idExpiryDate || undefined,
          ...(form.ssn && { ssn: form.ssn }),
          preferredLanguage: form.preferredLanguage || null,
          address: {
            country: form.addressCountry,
            region: form.addressRegion || null,
            city: form.addressCity,
            street: form.addressStreet,
            houseNumber: form.addressHouseNumber || null,
            postalCode: form.addressPostalCode || null,
          },
        },
      });
      if (result.status === 'verification' && result.kyc)
        return {
          success: true,
          message: result.message,
          providerUserReference: null,
          providerCardReference: null,
          verification: { ...result.kyc, application: { status: 'under_review', message: result.message } },
        };
      const cards = await api<{ id: string }[]>('GET', '/me/cards');
      return {
        success: true,
        message: result.message,
        providerUserReference: cards.at(-1)?.id ?? null,
        providerCardReference: cards.at(-1)?.id ?? null,
      };
    })(),
  );

/** The API's message (the password rules it broke, a wrong current password), else "Could not change your password." */
export async function changePassword(_email: string, currentPassword: string, newPassword: string) {
  try {
    await api('POST', '/auth/password/change', { body: { currentPassword, newPassword } });
    return { success: true, message: null };
  } catch (err) {
    if (!(err instanceof BffError)) throw err;
    return { success: false, message: err.message || 'Could not change your password.' };
  }
}

/** Identity verification at the processor (asked again each time: the questions are never kept by QOR). */
export const kycState = () => api<KycState>('GET', '/me/kyc');

/** The cardholder's answers to the processor's questions; where verification stands afterwards. */
export const answerKyc = (answers: { questionId: number; answerId: number }[]) =>
  api<KycState>('POST', '/me/kyc/answers', { body: { answers } });

/** What the application form must carry for this program's processor. */
export const onboardingRequirements = async () =>
  (await api<{ requirements?: { ssn: boolean } }>('GET', '/me/onboarding')).requirements ?? { ssn: false };

/** ZumRails takes applications on its own page: its address, from the cardholder's onboarding status. */
export const appConfig = async () => {
  const status = await api<{ hostedUrl?: string | null }>('GET', '/me/onboarding');
  return { zumRailsOnboardingUrl: status.hostedUrl ?? '' };
};
